import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/**
 * ✅ This component now does:
 * - Bulk paste codes → validate against cyl_info.productCode (supports / etc)
 * - Assign qty per code → batch save into stock_min_levels
 * - ✅ Sense-check view: show ALL existing stock_min_levels codes + targets (read-only)
 */

const CYL_INFO_LIMIT = 5000;

function pillClass(kind) {
  switch (kind) {
    case "ok":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "warn":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bad":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function Pill({ kind, children }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        pillClass(kind),
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function normaliseInputCodes(text) {
  const raw = String(text || "")
    .split(/[\n,\t\r ]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const seenUpper = new Set();
  const out = [];
  for (const c of raw) {
    const upper = c.toUpperCase();
    if (!seenUpper.has(upper)) {
      seenUpper.add(upper);
      out.push(c);
    }
  }
  return out;
}

function normaliseCode(code) {
  return String(code || "").trim();
}

function docIdForProductCode(code) {
  return encodeURIComponent(normaliseCode(code));
}

function decodeDocIdToProductCode(docId) {
  try {
    return decodeURIComponent(String(docId || ""));
  } catch {
    return String(docId || "");
  }
}

function formatTs(ts) {
  try {
    const d = ts?.toDate?.();
    if (d instanceof Date) return d.toLocaleString();
  } catch {}
  return "-";
}

export default function StockMinLevelsBulkAdd() {
  const [error, setError] = useState("");
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [saving, setSaving] = useState(false);

  const [paste, setPaste] = useState("");

  // cyl_info
  const [allCodesReal, setAllCodesReal] = useState([]);
  const [upperToReal, setUpperToReal] = useState(new Map());

  // parsed state
  const [valid, setValid] = useState([]); // [{codeReal, qty}]
  const [invalid, setInvalid] = useState([]); // [userCode]

  // read-only targets list
  const [showTargets, setShowTargets] = useState(false);
  const [targets, setTargets] = useState([]); // [{id, productCode, targetOnHand, updatedAt}]
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [targetSearch, setTargetSearch] = useState("");

  // Load cyl_info product codes once
  useEffect(() => {
    let cancelled = false;

    async function loadCodes() {
      setLoadingCodes(true);
      setError("");
      try {
        const snap = await getDocs(
          query(collection(db, "cyl_info"), orderBy("productCode", "asc"), limit(CYL_INFO_LIMIT))
        );

        const codes = [];
        const map = new Map();

        snap.forEach((d) => {
          const pc = d.data()?.productCode;
          const real = normaliseCode(pc);
          if (!real) return;

          codes.push(real);

          const upper = real.toUpperCase();
          if (!map.has(upper)) map.set(upper, real);
        });

        const unique = Array.from(new Set(codes)).filter(Boolean);

        if (!cancelled) {
          setAllCodesReal(unique);
          setUpperToReal(map);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoadingCodes(false);
      }
    }

    loadCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live subscribe to stock_min_levels (read-only list)
  useEffect(() => {
    setError("");
    setLoadingTargets(true);

    const q = query(collection(db, "stock_min_levels"), orderBy("productCode", "asc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          const productCode = normaliseCode(
            data.productCode || decodeDocIdToProductCode(d.id)
          );
          const targetOnHand = Number.isFinite(Number(data.targetOnHand))
            ? Number(data.targetOnHand)
            : 0;

          return {
            id: d.id,
            productCode,
            targetOnHand,
            updatedAt: data.updatedAt || null,
          };
        });

        setTargets(rows.filter((r) => r.productCode));
        setLoadingTargets(false);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoadingTargets(false);
      }
    );

    return () => unsub();
  }, []);

  function parseInput() {
    setError("");

    if (loadingCodes) {
      setError("Still loading cyl_info codes…");
      return;
    }

    const inputCodes = normaliseInputCodes(paste);

    const good = [];
    const bad = [];

    for (const c of inputCodes) {
      const upper = normaliseCode(c).toUpperCase();
      const real = upperToReal.get(upper);
      if (real) good.push({ codeReal: real, qty: "" });
      else bad.push(c);
    }

    setValid(good);
    setInvalid(bad);
  }

  function setQty(codeReal, qty) {
    setValid((prev) => prev.map((x) => (x.codeReal === codeReal ? { ...x, qty } : x)));
  }

  function fillAll(qty) {
    setValid((prev) => prev.map((x) => ({ ...x, qty })));
  }

  function removeRow(codeReal) {
    setValid((prev) => prev.filter((x) => x.codeReal !== codeReal));
  }

  async function saveAll() {
    setError("");

    const toSave = [];
    for (const row of valid) {
      const real = normaliseCode(row.codeReal);
      if (!real) continue;

      const n = Number(row.qty);
      if (!Number.isFinite(n) || n < 0) {
        setError(`Invalid qty for ${real}. Must be a number >= 0.`);
        return;
      }
      toSave.push({ codeReal: real, qty: n });
    }

    if (toSave.length === 0) {
      setError("Nothing to save.");
      return;
    }

    setSaving(true);
    try {
      const batch = writeBatch(db);

      for (const item of toSave) {
        const id = docIdForProductCode(item.codeReal);
        batch.set(
          doc(db, "stock_min_levels", id),
          {
            productCode: item.codeReal,
            targetOnHand: item.qty,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await batch.commit();

      setPaste("");
      setValid([]);
      setInvalid([]);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  const cylCount = allCodesReal.length;
  const validCount = valid.length;
  const invalidCount = invalid.length;

  const filteredTargets = useMemo(() => {
    const q = normaliseCode(targetSearch).toLowerCase();
    if (!q) return targets;
    return targets.filter((t) => t.productCode.toLowerCase().includes(q));
  }, [targets, targetSearch]);

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Bulk add / update stock targets</h2>
          <p className="mt-1 text-sm text-slate-600">
            Paste product codes (one per line / comma / space). Only codes that exist in{" "}
            <span className="font-mono">cyl_info.productCode</span> will be accepted. Codes can
            include <span className="font-mono">/</span> — saved safely using an encoded doc id.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Pill kind={loadingCodes ? "warn" : "ok"}>
              {loadingCodes ? "Loading cyl_info…" : `cyl_info codes: ${cylCount}`}
            </Pill>
            <Pill kind="ok">Valid: {validCount}</Pill>
            <Pill kind={invalidCount > 0 ? "warn" : "ok"}>Invalid: {invalidCount}</Pill>
            <Pill kind={loadingTargets ? "warn" : "ok"}>
              {loadingTargets ? "Loading targets…" : `Targets: ${targets.length}`}
            </Pill>
          </div>
        </div>

        <div className="w-full max-w-sm">
          <button
            onClick={() => setShowTargets((v) => !v)}
            className={[
              "w-full rounded-lg border px-4 py-2 text-sm font-semibold",
              showTargets
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            ].join(" ")}
          >
            {showTargets ? "Hide targets list" : "Show targets list"}
          </button>

          {error ? (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      </div>

      {/* Targets list (read-only) */}
      {showTargets ? (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Current stock_min_levels</div>
              <div className="text-xs text-slate-500">
                Read-only sanity check. (Doc id is encoded; we show real productCode.)
              </div>
            </div>

            <div className="w-full max-w-sm">
              <label className="text-xs font-medium text-slate-600">Search</label>
              <input
                value={targetSearch}
                onChange={(e) => setTargetSearch(e.target.value)}
                placeholder="filter by product code…"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
              />
            </div>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200">
            <div className="max-h-[320px] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-slate-600">
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 font-medium">Product code</th>
                    <th className="px-4 py-3 font-medium">Target</th>
                    <th className="px-4 py-3 font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTargets.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-mono">{t.productCode}</td>
                      <td className="px-4 py-3">
                        <Pill kind="ok">{t.targetOnHand}</Pill>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {t.updatedAt ? formatTs(t.updatedAt) : "-"}
                      </td>
                    </tr>
                  ))}

                  {!loadingTargets && filteredTargets.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                        No targets match that search.
                      </td>
                    </tr>
                  ) : null}

                  {loadingTargets ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                        Loading targets…
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Left: paste input */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <label className="text-xs font-medium text-slate-600">Paste codes</label>
          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={`TSMI200\nTSMI250\nTSMI150/HP\nTSMI300`}
            className="mt-1 h-56 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
          />

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={parseInput}
              disabled={loadingCodes}
              className={[
                "rounded-lg px-4 py-2 text-sm font-semibold",
                loadingCodes
                  ? "bg-slate-200 text-slate-500"
                  : "bg-slate-900 text-white hover:bg-slate-800",
              ].join(" ")}
            >
              Parse & validate
            </button>

            <button
              onClick={() => {
                setPaste("");
                setValid([]);
                setInvalid([]);
                setError("");
              }}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear
            </button>
          </div>

          {invalid.length > 0 ? (
            <div className="mt-4">
              <div className="text-xs font-medium text-slate-600">Invalid / not in cyl_info</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {invalid.slice(0, 80).map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-mono text-amber-700"
                    title="Not found in cyl_info.productCode"
                  >
                    {c}
                  </span>
                ))}
                {invalid.length > 80 ? (
                  <span className="text-xs text-slate-500">(+{invalid.length - 80} more)</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {/* Right: valid list + qty editing */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-xs font-medium text-slate-600">Valid codes</div>
              <div className="text-sm text-slate-600">
                Set quantities, then <span className="font-semibold">Save all</span>.
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Fill all"
                className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                onKeyDown={(e) => {
                  if (e.key === "Enter") fillAll(e.currentTarget.value);
                }}
              />
              <button
                onClick={(e) => {
                  const input = e.currentTarget.previousSibling;
                  if (input && input.value != null) fillAll(input.value);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-slate-200">
            {valid.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No valid codes yet.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-slate-600">
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-2 font-medium">Code</th>
                    <th className="px-3 py-2 font-medium">Target</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {valid.map((row) => (
                    <tr key={row.codeReal} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-mono">{row.codeReal}</td>
                      <td className="px-3 py-2">
                        <input
                          value={row.qty}
                          onChange={(e) => setQty(row.codeReal, e.target.value)}
                          inputMode="numeric"
                          className="w-28 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removeRow(row.codeReal)}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          title="Remove from list"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="mt-3 flex gap-2">
            <button
              disabled={saving || valid.length === 0}
              onClick={saveAll}
              className={[
                "rounded-lg px-4 py-2 text-sm font-semibold",
                saving || valid.length === 0
                  ? "bg-slate-200 text-slate-500"
                  : "bg-slate-900 text-white hover:bg-slate-800",
              ].join(" ")}
            >
              {saving ? "Saving…" : "Save all"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}